/**
 * Excessive Access User Table Component
 * Displays paginated, sortable table of users with excessive access insights
 */

import React from 'react';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Pagination from '@mui/material/Pagination';
import Typography from '@mui/material/Typography';
import { Eye, ArrowUp, ArrowDown } from 'lucide-react';

const ExcessiveAccessUserTable = ({
  users,
  loading,
  sortBy,
  sortOrder,
  currentPage,
  totalUsers,
  pageSize,
  onSort,
  onPageChange,
  onViewDetails
}) => {
  const getClassificationChip = (classification) => {
    const variants = {
      CRITICAL_EXCESS: { color: 'error', text: 'Critical' },
      HIGH_EXCESS: { color: 'warning', text: 'High' },
      MODERATE_EXCESS: { color: 'info', text: 'Moderate' },
      ACCEPTABLE: { color: 'success', text: 'Acceptable' }
    };

    const config = variants[classification] || { color: 'default', text: classification };

    return (
      <Chip
        label={config.text}
        color={config.color}
        size="small"
        sx={{ fontSize: '0.85rem' }}
      />
    );
  };

  const getRiskScoreChip = (score) => {
    let color = 'success';
    if (score >= 8) color = 'error';
    else if (score >= 6) color = 'warning';
    else if (score >= 4) color = 'info';

    return (
      <Chip
        label={score.toFixed(1)}
        color={color}
        size="small"
        sx={{ fontSize: '0.85rem' }}
      />
    );
  };

  const SortableHeader = ({ field, label }) => {
    const isActive = sortBy === field;
    return (
      <TableCell
        sx={{ cursor: 'pointer', userSelect: 'none' }}
        onClick={() => onSort(field)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {label}
          {isActive && (
            sortOrder === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
          )}
        </Box>
      </TableCell>
    );
  };

  const totalPages = Math.ceil(totalUsers / pageSize);

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 5 }}>
        <CircularProgress />
        <Typography sx={{ mt: 3 }}>Loading users...</Typography>
      </Box>
    );
  }

  if (!users || users.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 5 }}>
        <Typography color="text.secondary">No users found matching the current filters.</Typography>
      </Box>
    );
  }

  return (
    <>
      <TableContainer>
        <Table sx={{ fontSize: '0.9rem' }}>
          <TableHead sx={{ bgcolor: 'background.default' }}>
            <TableRow>
              <SortableHeader field="user_email" label="User" />
              <SortableHeader field="department" label="Department" />
              <SortableHeader field="peer_cohort_name" label="Cohort" />
              <SortableHeader field="classification" label="Classification" />
              <SortableHeader field="risk_score" label="Risk Score" />
              <TableCell>Groups</TableCell>
              <TableCell>Apps</TableCell>
              <TableCell>Unused %</TableCell>
              <TableCell sx={{ width: 100 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id} hover>
                <TableCell>
                  <div>
                    <strong>{user.display_name || user.user_email}</strong>
                  </div>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {user.user_email}
                  </Typography>
                </TableCell>
                <TableCell>{user.department || 'N/A'}</TableCell>
                <TableCell sx={{ fontSize: '0.85rem' }}>
                  {user.peer_cohort_name || 'N/A'}
                </TableCell>
                <TableCell>{getClassificationChip(user.classification)}</TableCell>
                <TableCell>{getRiskScoreChip(user.risk_score)}</TableCell>
                <TableCell>
                  <span style={{ fontWeight: 'bold' }}>{user.user_group_count}</span>
                  <Typography component="span" sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                    {' '}/ {user.peer_avg_group_count?.toFixed(1)}
                  </Typography>
                  <Chip
                    label={`z: ${user.group_count_z_score?.toFixed(2)}`}
                    size="small"
                    variant="outlined"
                    sx={{ ml: 1, fontSize: '0.75rem' }}
                  />
                </TableCell>
                <TableCell>
                  <span style={{ fontWeight: 'bold' }}>{user.user_app_count}</span>
                  <Typography component="span" sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                    {' '}/ {user.peer_avg_app_count?.toFixed(1)}
                  </Typography>
                  <Chip
                    label={`z: ${user.app_count_z_score?.toFixed(2)}`}
                    size="small"
                    variant="outlined"
                    sx={{ ml: 1, fontSize: '0.75rem' }}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={`${user.unused_access_percentage?.toFixed(0)}%`}
                    color={user.unused_access_percentage > 50 ? 'error' : 'warning'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => onViewDetails(user)}
                    startIcon={<Eye size={14} />}
                  >
                    Details
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 4 }}>
          <Typography sx={{ fontSize: '0.9rem', color: 'text.secondary' }}>
            Showing {(currentPage - 1) * pageSize + 1} to{' '}
            {Math.min(currentPage * pageSize, totalUsers)} of {totalUsers} users
          </Typography>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(e, page) => onPageChange(page)}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}
    </>
  );
};

export default ExcessiveAccessUserTable;
